import g from '#core/global';
import load from '#core/load';


const state = window.teal_state = window.teal_state || {};
state.filterable_list = state.filterable_list || {};

const component_attr = 'data-filterable-list';
const group_attr = 'data-filterable-list-group';
const toggle_attr = 'data-filterable-list-toggle';
const content_attr = 'data-filterable-list-content';
const expanded_attr = 'data-filterable-list-expanded';
const match_attr = 'data-filterable-list-match';
const filter_attr = 'data-filterable-list-filter';
const item_attr = 'data-filterable-list-item';
const text_attr = 'data-filterable-list-text';

const component_$ = `[${component_attr}]`;
const group_$ = `[${group_attr}]`;
const toggle_$ = `[${toggle_attr}]`;
const content_$ = `[${content_attr}]`;
const item_$ = `[${item_attr}]`;
const filter_$ = `[${filter_attr}]`;
const text_$ = `[${text_attr}]`;

const aria_expanded_attr = 'aria-expanded';

let has_component = false;


const module = {

	/**
	 * Init module
	 */
	init() {
		if (load.ran('filterable-list')) return false;

		load.content(() => {
			has_component = assign_elements();
			if (!has_component) return false;

			requestAnimationFrame(() => {
				initialize_all_components();
			});
		});

		load.all(() => {
			has_component = assign_elements();
			if (!has_component) return false;
		});
	},

};

export default module;
module.init();


/**
 * Assign document-level event listeners
 * @return {boolean}
 */
function assign_elements() {
	if (has_component) return true;

	if (!document.querySelector(component_$)) return false;

	document.addEventListener(g.click_e, e => {
		const $toggle = e.target.closest(toggle_$);
		if (!$toggle) return;

		const $comp = $toggle.closest(component_$);
		if (!$comp) return;

		const component_id = $comp.getAttribute(component_attr);
		toggle_group($toggle, $comp, component_id);
	});

	document.addEventListener(g.input_e, e => {
		const $input = e.target.closest(filter_$);
		if (!$input) return;

		const $comp = $input.closest(component_$);
		if (!$comp) return;

		const component_id = $comp.getAttribute(component_attr);
		const query = $input.value.trim().toLowerCase();

		if (query.length) {
			apply_filter(query, $comp, component_id);
		} else {
			clear_filter($comp, component_id);
		}
	});

	return true;
}


/**
 * Initialize section states for all components on the page
 */
function initialize_all_components() {
	document.querySelectorAll(component_$).forEach($comp => {
		const component_id = $comp.getAttribute(component_attr);
		initialize_component($comp, component_id);
	});
}


/**
 * Record initial expanded states for a component
 * @param {element} $comp
 * @param {string} component_id
 */
function initialize_component($comp, component_id) {
	const comp_state = get_state(component_id);

	$comp.querySelectorAll(content_$).forEach($content => {
		const group_id = $content.getAttribute(content_attr);
		const is_expanded = $content.getAttribute(expanded_attr) === g.true_s;
		comp_state.sections[group_id] = is_expanded;
	});
}


/**
 * Toggle a group section open or closed, updating its natural state
 * @param {element} $toggle
 * @param {element} $comp
 * @param {string} component_id
 */
function toggle_group($toggle, $comp, component_id) {
	const group_id = $toggle.getAttribute(toggle_attr);
	const $content = $comp.querySelector(`${content_$}[${content_attr}="${group_id}"]`);
	if (!$content) return;

	const is_expanded = $content.getAttribute(expanded_attr) === g.true_s;
	const new_state = !is_expanded;

	set_group_expanded($content, $toggle, new_state);

	// Only update natural state when not filtering
	const comp_state = get_state(component_id);
	if (!comp_state.is_filtering) {
		comp_state.sections[group_id] = new_state;
	}
}


/**
 * Filter items by query, opening collapsed groups that contain matches
 * @param {string} query
 * @param {element} $comp
 * @param {string} component_id
 */
function apply_filter(query, $comp, component_id) {
	const comp_state = get_state(component_id);
	comp_state.is_filtering = true;

	// Filter items within groups
	$comp.querySelectorAll(group_$).forEach($group => {
		const group_id = $group.getAttribute(group_attr);
		const $toggle = $comp.querySelector(`${toggle_$}[${toggle_attr}="${group_id}"]`);
		const $content = $comp.querySelector(`${content_$}[${content_attr}="${group_id}"]`);
		let group_has_match = false;

		$group.querySelectorAll(item_$).forEach($item => {
			const text = get_item_text($item);
			const matches = text.includes(query);
			$item.setAttribute(match_attr, matches ? g.true_s : g.false_s);
			if (matches) {
				group_has_match = true;
			}
		});

		// All children visible when the group label itself matches
		const group_label_el = $toggle ? $toggle.querySelector(text_$) : null;
		const group_label_text = group_label_el ? group_label_el.textContent.toLowerCase() : '';
		if (group_label_text.includes(query)) {
			$group.querySelectorAll(item_$).forEach($item => {
				$item.setAttribute(match_attr, g.true_s);
			});
			group_has_match = true;
		}

		$group.setAttribute(match_attr, group_has_match ? g.true_s : g.false_s);

		if ($content && $toggle) {
			if (group_has_match && $content.getAttribute(expanded_attr) === g.false_s) {
				set_group_expanded($content, $toggle, true);
			}
		}
	});

	// Filter top-level flat items (not inside a group)
	$comp.querySelectorAll(`:scope > ul > ${item_$}`).forEach($item => {
		const text = get_item_text($item);
		const matches = text.includes(query);
		$item.setAttribute(match_attr, matches ? g.true_s : g.false_s);
	});
}


/**
 * Clear the filter and restore natural section states
 * @param {element} $comp
 * @param {string} component_id
 */
function clear_filter($comp, component_id) {
	const comp_state = get_state(component_id);
	comp_state.is_filtering = false;

	// Remove match attributes from all groups and items
	$comp.querySelectorAll(`[${match_attr}]`).forEach($el => {
		$el.removeAttribute(match_attr);
	});

	// Restore natural section states
	$comp.querySelectorAll(content_$).forEach($content => {
		const group_id = $content.getAttribute(content_attr);
		const $toggle = $comp.querySelector(`${toggle_$}[${toggle_attr}="${group_id}"]`);
		const should_expand = comp_state.sections[group_id] ?? false;
		const is_expanded = $content.getAttribute(expanded_attr) === g.true_s;

		if (should_expand !== is_expanded && $toggle) {
			set_group_expanded($content, $toggle, should_expand);
		}
	});
}


/**
 * Set a group's expanded state with animated height transition
 * @param {element} $content
 * @param {element} $toggle
 * @param {boolean} expand
 */
function set_group_expanded($content, $toggle, expand) {
	$toggle.setAttribute(aria_expanded_attr, expand ? g.true_s : g.false_s);
	$content.setAttribute(expanded_attr, expand ? g.true_s : g.false_s);

	if (expand) {
		$content.removeAttribute(g.hidden_attr);

		const height = $content.scrollHeight;
		$content.style.height = '0px';
		$content.offsetHeight;
		$content.style.height = height + 'px';

		const on_done = () => {
			$content.removeAttribute('style');
			$content.removeEventListener(g.transition_end_e, on_done);
		};
		$content.addEventListener(g.transition_end_e, on_done);

	} else {
		const height = $content.scrollHeight;
		$content.style.height = height + 'px';
		$content.offsetHeight;
		$content.style.height = '0px';

		const on_done = () => {
			$content.setAttribute(g.hidden_attr, '');
			$content.removeAttribute('style');
			$content.removeEventListener(g.transition_end_e, on_done);
		};
		$content.addEventListener(g.transition_end_e, on_done);
	}
}


/**
 * Get the filterable text string from an item element
 * @param {element} $item
 * @return {string}
 */
function get_item_text($item) {
	const $text_el = $item.querySelector(text_$);
	const raw = $text_el ? $text_el.textContent : $item.textContent;
	return raw.toLowerCase();
}


/**
 * Get or create per-component state object
 * @param {string} component_id
 * @return {object}
 */
function get_state(component_id) {
	if (!state.filterable_list[component_id]) {
		state.filterable_list[component_id] = {
			sections: {},
			is_filtering: false,
		};
	}
	return state.filterable_list[component_id];
}
